import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTenant } from '../contexts/TenantContext';
import { useDashboardData } from '../contexts/DashboardDataContext';
import GooglePlacesSearch from '../components/GooglePlacesSearch';
import {
  Star,
  MessageSquare,
  Send,
  Settings,
  Loader2,
  CheckCircle,
  AlertCircle,
  ExternalLink,
  ChevronDown,
  Search,
  Shield,
  Zap,
  MessageCircle,
  BarChart3,
  X,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ReviewsPage() {
  const { getToken } = useTenant();
  const { googleReviewStats, reviewSubscription, refreshReviewData } = useDashboardData();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [reviewStatus, setReviewStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(false);
  const [showSuccessBanner, setShowSuccessBanner] = useState(false);

  // Check for subscription success/cancel from Stripe redirect
  useEffect(() => {
    const sub = searchParams.get('subscription');
    if (sub === 'success') {
      setShowSuccessBanner(true);
      refreshReviewData();
      // Clean URL
      searchParams.delete('subscription');
      setSearchParams(searchParams, { replace: true });
    } else if (sub === 'cancelled') {
      searchParams.delete('subscription');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, refreshReviewData]);

  // Fetch connection status
  const fetchStatus = useCallback(async () => {
    try {
      const token = await getToken();
      const res = await fetch(`${BACKEND_URL}/api/reviews/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setReviewStatus(await res.json());
    } catch (err) {
      console.error('Reviews status error:', err);
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  if (loading) {
    return (
      <div className="p-4 lg:p-8 flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
      </div>
    );
  }

  const hasPlaceId = googleReviewStats?.has_place_id;
  const subStatus = reviewSubscription?.status || reviewStatus?.review_mgmt_status || 'inactive';
  const isSubscribed = subStatus === 'active';
  const isConnected = reviewStatus?.connected;

  // ── State 1: No google_place_id ──
  if (!hasPlaceId) {
    return (
      <NoPlaceIdView getToken={getToken} refreshReviewData={refreshReviewData} />
    );
  }

  // ── State 2: Has google_place_id but no active subscription (free tier) ──
  if (!isSubscribed) {
    return (
      <FreeeTierView
        googleReviewStats={googleReviewStats}
        subscribing={subscribing}
        setSubscribing={setSubscribing}
        getToken={getToken}
        showSuccessBanner={showSuccessBanner}
        setShowSuccessBanner={setShowSuccessBanner}
      />
    );
  }

  // ── State 3: Active subscription (paid tier) ──
  return (
    <PaidTierView
      getToken={getToken}
      reviewStatus={reviewStatus}
      fetchStatus={fetchStatus}
      isConnected={isConnected}
      googleReviewStats={googleReviewStats}
      showSuccessBanner={showSuccessBanner}
      setShowSuccessBanner={setShowSuccessBanner}
      refreshReviewData={refreshReviewData}
    />
  );
}


// ════════════════════════════════════════════
// No Place ID View (State 1)
// ════════════════════════════════════════════

function NoPlaceIdView({ getToken, refreshReviewData }) {
  const [googlePlace, setGooglePlace] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSave = async () => {
    if (!googlePlace?.place_id) return;
    setSaving(true);
    setError(null);
    try {
      const token = await getToken();
      const res = await fetch(`${BACKEND_URL}/api/reviews/save-place-id`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ google_place_id: googlePlace.place_id }),
      });
      if (res.ok) {
        await refreshReviewData();
      } else {
        const errData = await res.json().catch(() => ({}));
        setError(errData.detail || 'Failed to save');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4 lg:p-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Google Reviews</h1>
        <p className="text-gray-600 mt-1">Manage your Google reviews and online reputation</p>
      </div>

      <div className="max-w-lg mx-auto mt-12">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 text-center">
          <div className="w-16 h-16 bg-yellow-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Star className="w-8 h-8 text-yellow-500" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Find Your Business on Google</h2>
          <p className="text-gray-600 text-sm mb-6">
            Search for your business to see your Google rating and review count.
          </p>

          <div className="text-left mb-4">
            <GooglePlacesSearch
              onSelect={setGooglePlace}
              getToken={getToken}
              placeholder="Search your business name..."
            />
          </div>

          {googlePlace && (
            <div className="text-left mb-4 p-3 bg-gray-50 rounded-lg">
              <p className="text-sm font-medium text-gray-900">{googlePlace.description || googlePlace.structured_name?.main}</p>
              {googlePlace.structured_name?.secondary && (
                <p className="text-xs text-gray-500 mt-0.5">{googlePlace.structured_name.secondary}</p>
              )}
            </div>
          )}

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}

          <button
            onClick={handleSave}
            disabled={saving || !googlePlace}
            className="w-full py-3 px-4 bg-gradient-to-r from-orange-500 to-orange-600 text-white font-medium rounded-lg hover:from-orange-600 hover:to-orange-700 disabled:opacity-50 transition-all"
          >
            {saving ? (
              <span className="flex items-center justify-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                Connecting...
              </span>
            ) : (
              'Connect Business'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}


// ════════════════════════════════════════════
// Free Tier View
// ════════════════════════════════════════════

function FreeeTierView({ googleReviewStats, subscribing, setSubscribing, getToken, showSuccessBanner, setShowSuccessBanner }) {
  const rating = googleReviewStats?.rating || 0;
  const reviewCount = googleReviewStats?.review_count || 0;
  const googleMapsUrl = googleReviewStats?.google_maps_url || '';

  const handleSubscribe = async () => {
    setSubscribing(true);
    try {
      const token = await getToken();
      const res = await fetch(`${BACKEND_URL}/api/reviews/subscribe`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (res.ok) {
        const data = await res.json();
        if (data.checkout_url) {
          window.location.href = data.checkout_url;
          return;
        }
      } else {
        const errData = await res.json().catch(() => ({}));
        alert(errData.detail || 'Failed to start subscription');
      }
    } catch (err) {
      console.error('Subscribe error:', err);
      alert('Failed to start subscription');
    } finally {
      setSubscribing(false);
    }
  };

  return (
    <div className="p-4 lg:p-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Reviews</h1>
        <p className="text-gray-600 mt-1">Your Google review snapshot</p>
      </div>

      {showSuccessBanner && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <span className="text-green-800 font-medium">Subscription activated! Loading your review management tools...</span>
          </div>
          <button onClick={() => setShowSuccessBanner(false)}>
            <X className="w-4 h-4 text-green-600" />
          </button>
        </div>
      )}

      <div className="max-w-2xl mx-auto space-y-6">
        {/* Google Rating Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex flex-col sm:flex-row items-center gap-6">
            <div className="text-center">
              <div className="text-5xl font-bold text-gray-900">{rating || '--'}</div>
              <div className="flex items-center justify-center gap-0.5 mt-2">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Star
                    key={i}
                    className={`w-5 h-5 ${
                      i <= Math.round(rating) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'
                    }`}
                  />
                ))}
              </div>
              <div className="text-sm text-gray-500 mt-1">{reviewCount} Google reviews</div>
            </div>
            <div className="flex-1 text-center sm:text-left">
              <h3 className="text-lg font-semibold text-gray-900">Your Google Rating</h3>
              <p className="text-sm text-gray-600 mt-1">
                This is your current public rating on Google. Upgrade to manage and respond to reviews directly.
              </p>
              {googleMapsUrl && (
                <a
                  href={googleMapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-sm text-orange-600 hover:text-orange-700 font-medium mt-3"
                >
                  View on Google
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
              )}
            </div>
          </div>
        </div>

        {/* Upgrade CTA Card */}
        <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl border border-orange-200 p-6">
          <div className="flex items-start gap-4 mb-5">
            <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <Zap className="w-6 h-6 text-orange-600" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">Unlock Review Management</h3>
              <p className="text-sm text-gray-600 mt-1">
                Take control of your online reputation for <span className="font-semibold text-gray-900">$29.99/mo</span> + GST
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
            {[
              { icon: MessageCircle, text: 'Reply to reviews directly' },
              { icon: Send, text: 'Send review requests to customers' },
              { icon: BarChart3, text: 'Track review stats & trends' },
              { icon: Shield, text: 'Private feedback management' },
            ].map(({ icon: Icon, text }) => (
              <div key={text} className="flex items-center gap-2.5">
                <div className="w-7 h-7 bg-white rounded-lg flex items-center justify-center shadow-sm">
                  <Icon className="w-4 h-4 text-orange-600" />
                </div>
                <span className="text-sm text-gray-700">{text}</span>
              </div>
            ))}
          </div>

          <button
            onClick={handleSubscribe}
            disabled={subscribing}
            className="w-full py-3 px-4 bg-gradient-to-r from-orange-500 to-orange-600 text-white font-medium rounded-lg hover:from-orange-600 hover:to-orange-700 disabled:opacity-50 transition-all shadow-sm"
          >
            {subscribing ? (
              <span className="flex items-center justify-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                Redirecting to checkout...
              </span>
            ) : (
              'Activate Review Management'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}


// ════════════════════════════════════════════
// Paid Tier View (full management)
// ════════════════════════════════════════════

function PaidTierView({ getToken, reviewStatus, fetchStatus, isConnected, googleReviewStats, showSuccessBanner, setShowSuccessBanner, refreshReviewData }) {
  const [activeTab, setActiveTab] = useState('overview');
  const [connecting, setConnecting] = useState(false);
  const [connectError, setConnectError] = useState(null);
  const [magicLinkLoading, setMagicLinkLoading] = useState(false);

  const googleConnected = reviewStatus?.google_connected;

  const handleConnect = async () => {
    setConnecting(true);
    setConnectError(null);
    try {
      const token = await getToken();
      const res = await fetch(`${BACKEND_URL}/api/reviews/connect`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.detail || 'Connection failed');
      }
      await fetchStatus();
    } catch (err) {
      setConnectError(err.message);
    } finally {
      setConnecting(false);
    }
  };

  const handleMagicLink = async () => {
    setMagicLinkLoading(true);
    try {
      const token = await getToken();
      const res = await fetch(`${BACKEND_URL}/api/reviews/magic-link`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        if (data.login_url) {
          window.open(data.login_url, '_blank');
        }
      } else {
        const errData = await res.json().catch(() => ({}));
        alert(errData.detail || 'Failed to get login link');
      }
    } catch (err) {
      console.error('Magic link error:', err);
    } finally {
      setMagicLinkLoading(false);
    }
  };

  // If not connected to Climbo yet, show the connect screen
  if (!isConnected) {
    return (
      <div className="p-4 lg:p-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Reviews</h1>
          <p className="text-gray-600 mt-1">Set up your review management</p>
        </div>

        {showSuccessBanner && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <span className="text-green-800 font-medium">Subscription activated! Now connect your Google account below.</span>
            </div>
            <button onClick={() => setShowSuccessBanner(false)}>
              <X className="w-4 h-4 text-green-600" />
            </button>
          </div>
        )}

        {/* Google Rating Summary (from free tier data) */}
        {googleReviewStats?.has_place_id && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-6 flex items-center gap-4">
            <div className="flex items-center gap-1">
              <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
              <span className="text-lg font-bold text-gray-900">{googleReviewStats.rating || '--'}</span>
            </div>
            <span className="text-sm text-gray-500">{googleReviewStats.review_count} reviews on Google</span>
          </div>
        )}

        <div className="max-w-lg mx-auto mt-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 text-center">
            <div className="w-16 h-16 bg-orange-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Settings className="w-8 h-8 text-orange-500" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Connect Google Reviews</h2>
            <p className="text-gray-600 text-sm mb-4">
              Step 1: Provision your review management account, then connect your Google Business Profile via the magic link.
            </p>

            {connectError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {connectError}
              </div>
            )}

            <button
              onClick={handleConnect}
              disabled={connecting}
              className="w-full py-3 px-4 bg-gradient-to-r from-orange-500 to-orange-600 text-white font-medium rounded-lg hover:from-orange-600 hover:to-orange-700 disabled:opacity-50 transition-all mb-3"
            >
              {connecting ? (
                <span className="flex items-center justify-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Provisioning...
                </span>
              ) : (
                'Connect Google Reviews'
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // If connected to Climbo but Google OAuth not completed
  const needsGoogleOAuth = isConnected && !googleConnected;

  const tabs = [
    { id: 'overview', label: 'Overview', icon: Star },
    { id: 'inbox', label: 'Inbox', icon: MessageSquare },
    { id: 'request', label: 'Request Reviews', icon: Send },
    { id: 'settings', label: 'Settings', icon: Settings },
  ];

  return (
    <div className="p-4 lg:p-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Reviews</h1>
        <p className="text-gray-600 mt-1">Manage your online reputation</p>
      </div>

      {showSuccessBanner && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <span className="text-green-800 font-medium">Review management is active!</span>
          </div>
          <button onClick={() => setShowSuccessBanner(false)}>
            <X className="w-4 h-4 text-green-600" />
          </button>
        </div>
      )}

      {/* Google OAuth banner */}
      {needsGoogleOAuth && (
        <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0" />
            <div>
              <p className="text-amber-900 font-medium text-sm">Google account not connected yet</p>
              <p className="text-amber-700 text-xs mt-0.5">Click the button to sign in with Google and authorize review access.</p>
            </div>
          </div>
          <button
            onClick={handleMagicLink}
            disabled={magicLinkLoading}
            className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 text-white text-sm font-medium rounded-lg hover:bg-amber-700 disabled:opacity-50 whitespace-nowrap"
          >
            {magicLinkLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />}
            Connect Google Account
          </button>
        </div>
      )}

      {/* Tab bar */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex gap-6 -mb-px">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 pb-3 text-sm font-medium border-b-2 transition-colors ${
                  active
                    ? 'border-orange-500 text-orange-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab content */}
      {activeTab === 'overview' && <OverviewTab getToken={getToken} />}
      {activeTab === 'inbox' && <InboxTab getToken={getToken} />}
      {activeTab === 'request' && <RequestTab getToken={getToken} />}
      {activeTab === 'settings' && (
        <SettingsTab getToken={getToken} status={reviewStatus} onRefresh={fetchStatus} refreshReviewData={refreshReviewData} />
      )}
    </div>
  );
}


// ── Overview Tab ──
function OverviewTab({ getToken }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const token = await getToken();
        const res = await fetch(`${BACKEND_URL}/api/reviews/stats`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) setStats(await res.json());
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    })();
  }, [getToken]);

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-6 h-6 text-orange-500 animate-spin" />
      </div>
    );
  }

  if (!stats) return null;

  const dist = stats.rating_distribution || {};
  const maxDist = Math.max(...Object.values(dist), 1);

  return (
    <div className="space-y-6">
      {/* Big rating display */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 flex flex-col sm:flex-row items-center gap-6">
        <div className="text-center">
          <div className="text-5xl font-bold text-gray-900">{stats.average_rating || '--'}</div>
          <div className="flex items-center justify-center gap-0.5 mt-2">
            {[1, 2, 3, 4, 5].map((i) => (
              <Star
                key={i}
                className={`w-5 h-5 ${
                  i <= Math.round(stats.average_rating)
                    ? 'text-yellow-400 fill-yellow-400'
                    : 'text-gray-300'
                }`}
              />
            ))}
          </div>
          <div className="text-sm text-gray-500 mt-1">{stats.total_reviews} reviews</div>
        </div>

        {/* Rating distribution */}
        <div className="flex-1 w-full space-y-1.5">
          {[5, 4, 3, 2, 1].map((r) => {
            const count = dist[String(r)] || 0;
            const pct = maxDist > 0 ? (count / maxDist) * 100 : 0;
            return (
              <div key={r} className="flex items-center gap-2 text-sm">
                <span className="w-3 text-gray-600 text-right">{r}</span>
                <Star className="w-3 h-3 text-yellow-400 fill-yellow-400" />
                <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-yellow-400 rounded-full"
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <span className="w-6 text-gray-500 text-xs">{count}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Total Reviews', value: stats.total_reviews, color: 'orange' },
          { label: 'Average Rating', value: stats.average_rating || '--', color: 'yellow' },
          { label: 'Unreplied', value: stats.unreplied_count, color: 'red' },
        ].map((s) => (
          <div
            key={s.label}
            className="bg-white rounded-xl shadow-sm border border-gray-100 p-4"
          >
            <div className="text-xs text-gray-500 font-medium">{s.label}</div>
            <div className="text-2xl font-bold text-gray-900 mt-1">{s.value}</div>
          </div>
        ))}
      </div>
    </div>
  );
}


// ── Inbox Tab ──
function InboxTab({ getToken }) {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [replySending, setReplySending] = useState(false);
  const [sourceFilter, setSourceFilter] = useState('');
  const [total, setTotal] = useState(0);

  const fetchReviews = useCallback(async () => {
    setLoading(true);
    try {
      const token = await getToken();
      const params = new URLSearchParams({ page: String(page) });
      if (sourceFilter) params.set('source', sourceFilter);
      const res = await fetch(`${BACKEND_URL}/api/reviews?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setReviews(data.reviews || data.data || []);
        setTotal(data.total || 0);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [getToken, page, sourceFilter]);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  const handleReply = async (reviewId) => {
    if (!replyText.trim()) return;
    setReplySending(true);
    try {
      const token = await getToken();
      const res = await fetch(`${BACKEND_URL}/api/reviews/${reviewId}/reply`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ reply_text: replyText }),
      });
      if (res.ok) {
        setReplyingTo(null);
        setReplyText('');
        fetchReviews();
      }
    } catch (err) {
      console.error(err);
    } finally {
      setReplySending(false);
    }
  };

  if (loading && reviews.length === 0) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-6 h-6 text-orange-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filter bar */}
      <div className="flex items-center gap-3">
        <div className="relative">
          <select
            value={sourceFilter}
            onChange={(e) => {
              setSourceFilter(e.target.value);
              setPage(1);
            }}
            className="appearance-none pl-3 pr-8 py-2 border border-gray-200 rounded-lg text-sm focus:border-orange-400 focus:outline-none bg-white"
          >
            <option value="">All sources</option>
            <option value="google">Google</option>
            <option value="facebook">Facebook</option>
          </select>
          <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        </div>
        <span className="text-sm text-gray-500">{total} reviews</span>
      </div>

      {/* Review list */}
      {reviews.length === 0 ? (
        <div className="text-center py-12">
          <MessageSquare className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 text-sm">No reviews found</p>
        </div>
      ) : (
        <div className="space-y-3">
          {reviews.map((review) => {
            const id = review.id || review._id;
            return (
              <div
                key={id}
                className="bg-white rounded-xl shadow-sm border border-gray-100 p-4"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm text-gray-900">
                        {review.reviewer_name || review.author || 'Anonymous'}
                      </span>
                      <div className="flex items-center gap-0.5">
                        {[1, 2, 3, 4, 5].map((i) => (
                          <Star
                            key={i}
                            className={`w-3 h-3 ${
                              i <= (review.rating || 0)
                                ? 'text-yellow-400 fill-yellow-400'
                                : 'text-gray-300'
                            }`}
                          />
                        ))}
                      </div>
                      {review.source && (
                        <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full capitalize">
                          {review.source}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-700 mt-1">{review.text || review.comment || ''}</p>
                    <p className="text-xs text-gray-400 mt-1">
                      {review.date || review.created_at
                        ? new Date(review.date || review.created_at).toLocaleDateString('en-AU', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                          })
                        : ''}
                    </p>

                    {/* Existing reply */}
                    {review.reply && (
                      <div className="mt-2 pl-3 border-l-2 border-orange-200">
                        <p className="text-xs text-gray-500 font-medium">Your reply</p>
                        <p className="text-sm text-gray-700">{review.reply}</p>
                      </div>
                    )}
                  </div>

                  {/* Reply button */}
                  {!review.reply && (
                    <button
                      onClick={() => {
                        setReplyingTo(replyingTo === id ? null : id);
                        setReplyText('');
                      }}
                      className="text-xs text-orange-600 hover:text-orange-700 font-medium whitespace-nowrap"
                    >
                      Reply
                    </button>
                  )}
                </div>

                {/* Reply form */}
                {replyingTo === id && (
                  <div className="mt-3 flex gap-2">
                    <textarea
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      placeholder="Write your reply..."
                      rows={2}
                      className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:border-orange-400 focus:outline-none resize-none"
                    />
                    <button
                      onClick={() => handleReply(id)}
                      disabled={replySending || !replyText.trim()}
                      className="px-4 py-2 bg-orange-500 text-white text-sm rounded-lg hover:bg-orange-600 disabled:opacity-50 self-end"
                    >
                      {replySending ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send'}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {total > 50 && (
        <div className="flex items-center justify-center gap-3 pt-4">
          <button
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
            className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            Previous
          </button>
          <span className="text-sm text-gray-500">Page {page}</span>
          <button
            onClick={() => setPage((p) => p + 1)}
            className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}


// ── Request Reviews Tab ──
function RequestTab({ getToken }) {
  const [customers, setCustomers] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [method, setMethod] = useState('sms');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const token = await getToken();
        const res = await fetch(`${BACKEND_URL}/api/customers?page_size=500`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setCustomers(data.customers || []);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    })();
  }, [getToken]);

  const filteredCustomers = customers.filter((c) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      (c.first_name || '').toLowerCase().includes(q) ||
      (c.last_name || '').toLowerCase().includes(q) ||
      (c.phone || '').includes(q) ||
      (c.email || '').toLowerCase().includes(q)
    );
  });

  const toggleCustomer = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleSend = async () => {
    if (selectedIds.length === 0) return;
    setSending(true);
    setResult(null);
    try {
      const token = await getToken();
      const res = await fetch(`${BACKEND_URL}/api/reviews/request`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ customer_ids: selectedIds, method }),
      });
      if (res.ok) {
        const data = await res.json();
        setResult(data);
        setSelectedIds([]);
      } else {
        const d = await res.json();
        setResult({ error: d.detail || 'Failed to send' });
      }
    } catch (err) {
      setResult({ error: err.message });
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-6 h-6 text-orange-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Method toggle */}
      <div className="flex items-center gap-2">
        <span className="text-sm text-gray-600">Send via:</span>
        {['sms', 'email'].map((m) => (
          <button
            key={m}
            onClick={() => setMethod(m)}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
              method === m
                ? 'bg-orange-500 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {m.toUpperCase()}
          </button>
        ))}
      </div>

      {/* Customer search + list */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search customers..."
            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:border-orange-400 focus:outline-none"
          />
        </div>

        <div className="max-h-80 overflow-y-auto space-y-1">
          {filteredCustomers.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-4">No customers found</p>
          ) : (
            filteredCustomers.map((c) => {
              const checked = selectedIds.includes(c.id);
              const contactOk = method === 'sms' ? c.phone : c.email;
              return (
                <label
                  key={c.id}
                  className={`flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer hover:bg-gray-50 ${
                    !contactOk ? 'opacity-50' : ''
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={!contactOk}
                    onChange={() => toggleCustomer(c.id)}
                    className="rounded border-gray-300 text-orange-500 focus:ring-orange-400"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900 truncate">
                      {c.first_name || ''} {c.last_name || ''}
                    </div>
                    <div className="text-xs text-gray-500 truncate">
                      {method === 'sms' ? c.phone || 'No phone' : c.email || 'No email'}
                    </div>
                  </div>
                </label>
              );
            })
          )}
        </div>
      </div>

      {/* Result banner */}
      {result && (
        <div
          className={`p-3 rounded-lg text-sm flex items-center gap-2 ${
            result.error
              ? 'bg-red-50 border border-red-200 text-red-700'
              : 'bg-green-50 border border-green-200 text-green-700'
          }`}
        >
          {result.error ? (
            <>
              <AlertCircle className="w-4 h-4" />
              {result.error}
            </>
          ) : (
            <>
              <CheckCircle className="w-4 h-4" />
              Sent {result.sent} review request{result.sent !== 1 ? 's' : ''} successfully!
            </>
          )}
        </div>
      )}

      {/* Send button */}
      <button
        onClick={handleSend}
        disabled={sending || selectedIds.length === 0}
        className="w-full sm:w-auto px-6 py-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white font-medium rounded-lg hover:from-orange-600 hover:to-orange-700 disabled:opacity-50 transition-all"
      >
        {sending ? (
          <span className="flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" />
            Sending...
          </span>
        ) : (
          `Send Review Requests (${selectedIds.length})`
        )}
      </button>
    </div>
  );
}


// ── Settings Tab ──
function SettingsTab({ getToken, status, onRefresh, refreshReviewData }) {
  const [magicLinkLoading, setMagicLinkLoading] = useState(false);
  const [magicLinkUrl, setMagicLinkUrl] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  const handleGetMagicLink = async () => {
    setMagicLinkLoading(true);
    try {
      const token = await getToken();
      const res = await fetch(`${BACKEND_URL}/api/reviews/magic-link`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        if (data.login_url) setMagicLinkUrl(data.login_url);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setMagicLinkLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel your review management subscription? You will lose access at the end of your billing period.')) return;
    setCancelling(true);
    try {
      const token = await getToken();
      const res = await fetch(`${BACKEND_URL}/api/reviews/cancel-subscription`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (res.ok) {
        alert('Subscription cancelled. Access continues until end of billing period.');
        if (refreshReviewData) refreshReviewData();
      } else {
        const errData = await res.json().catch(() => ({}));
        alert(errData.detail || 'Failed to cancel');
      }
    } catch (err) {
      console.error(err);
      alert('Failed to cancel subscription');
    } finally {
      setCancelling(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Connection status */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Connection Status</h3>

        <div className="space-y-3">
          <div className="flex items-center justify-between py-2">
            <span className="text-sm text-gray-600">Review Management</span>
            {status?.connected ? (
              <span className="flex items-center gap-1.5 text-sm text-green-600 font-medium">
                <CheckCircle className="w-4 h-4" />
                Connected
              </span>
            ) : (
              <span className="text-sm text-gray-400">Not connected</span>
            )}
          </div>

          <div className="flex items-center justify-between py-2">
            <span className="text-sm text-gray-600">Google Business Profile</span>
            {status?.google_connected ? (
              <span className="flex items-center gap-1.5 text-sm text-green-600 font-medium">
                <CheckCircle className="w-4 h-4" />
                Connected
              </span>
            ) : status?.google_place_id ? (
              <span className="text-sm text-yellow-600">Place ID set, not connected</span>
            ) : (
              <span className="text-sm text-gray-400">Not set up</span>
            )}
          </div>

          <div className="flex items-center justify-between py-2">
            <span className="text-sm text-gray-600">Subscription</span>
            <span className="flex items-center gap-1.5 text-sm text-green-600 font-medium">
              <CheckCircle className="w-4 h-4" />
              Active ($29.99/mo)
            </span>
          </div>
        </div>
      </div>

      {/* Advanced dashboard link */}
      {status?.connected && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Advanced Dashboard</h3>
          <p className="text-sm text-gray-600 mb-4">
            Access the full review management dashboard for advanced features.
          </p>
          {magicLinkUrl ? (
            <a
              href={magicLinkUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm text-orange-600 hover:text-orange-700 font-medium"
            >
              Open Dashboard
              <ExternalLink className="w-4 h-4" />
            </a>
          ) : (
            <button
              onClick={handleGetMagicLink}
              disabled={magicLinkLoading}
              className="inline-flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              {magicLinkLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <ExternalLink className="w-4 h-4" />
              )}
              Get Dashboard Link
            </button>
          )}
        </div>
      )}

      {/* Cancel subscription */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Subscription Management</h3>
        <p className="text-sm text-gray-600 mb-4">
          Cancel your review management subscription. Access continues until the end of your current billing period.
        </p>
        <button
          onClick={handleCancelSubscription}
          disabled={cancelling}
          className="inline-flex items-center gap-2 px-4 py-2 border border-red-200 text-red-600 rounded-lg text-sm hover:bg-red-50 disabled:opacity-50"
        >
          {cancelling ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
          Cancel Subscription
        </button>
      </div>
    </div>
  );
}
